/**
 * BP-026 · Security console — MFA + IP allowlist for the CURRENT user.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Shield, Trash2, Copy, Plus } from "lucide-react";
import { apiGet, apiPost, apiDelete } from "@/lib/api";

export default function PlatformSecurity() {
  const [mfa, setMfa] = useState(null);
  const [uri, setUri] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState([]);
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState({ cidr: "", label: "" });

  const load = async () => {
    setMfa(await apiGet("/api/v3/platform/mfa/status"));
    setIps(await apiGet("/api/v3/platform/ip-allowlist"));
  };
  useEffect(() => {
    load();
  }, []);

  const enrol = async () => {
    try {
      const r = await apiPost("/api/v3/platform/mfa/enrol", {});
      setUri(r.otpauth_uri);
      toast.success("Scan the QR / copy the URI into your authenticator");
    } catch (e) {
      toast.error(e?.message || "Enrol failed");
    }
  };
  const verify = async () => {
    if (!code) return;
    try {
      const r = await apiPost("/api/v3/platform/mfa/verify", { code });
      setBackup(r.backup_codes || []);
      setUri(null);
      setCode("");
      toast.success("MFA verified — save your backup codes!");
      load();
    } catch (e) {
      toast.error(e?.message || "Verify failed");
    }
  };
  const revoke = async () => {
    if (!confirm("Revoke MFA?")) return;
    await apiPost("/api/v3/platform/mfa/revoke", {});
    setBackup([]);
    load();
  };
  const addIp = async () => {
    if (!newIp.cidr) return;
    try {
      await apiPost("/api/v3/platform/ip-allowlist", newIp);
      setNewIp({ cidr: "", label: "" });
      load();
    } catch (e) {
      toast.error(e?.message || "Add failed");
    }
  };
  const removeIp = async (id) => {
    await apiDelete(`/api/v3/platform/ip-allowlist/${id}`);
    load();
  };

  return (
    <div data-testid="platform-security" className="p-8 space-y-6 max-w-3xl">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <Shield className="w-4 h-4" />
          My security
        </div>
        <h1 className="text-2xl font-bold">MFA & IP allowlist</h1>
      </header>

      {/* MFA */}
      <section
        data-testid="mfa-section"
        className="rounded-xl border border-slate-200 bg-white p-5"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold flex items-center gap-1">
              <KeyRound className="w-4 h-4" /> MFA (TOTP)
            </div>
            <div className="text-xs text-slate-600 mt-1">
              {mfa?.verified
                ? `Verified at ${mfa.verified_at?.slice(0, 19)} · ${mfa.backup_codes_remaining} backup codes left`
                : mfa?.enrolled
                ? "Enrolled but not verified"
                : "Not enrolled"}
            </div>
          </div>
          {!mfa?.enrolled && (
            <button
              data-testid="mfa-enrol-btn"
              onClick={enrol}
              className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-900 px-3 py-1.5 text-sm"
            >
              Enrol
            </button>
          )}
          {mfa?.verified && (
            <button
              data-testid="mfa-revoke-btn"
              onClick={revoke}
              className="rounded-md border border-red-700 text-red-300 hover:bg-red-900/40 px-3 py-1.5 text-sm"
            >
              Revoke
            </button>
          )}
        </div>
        {uri && (
          <div className="mt-4 rounded-md bg-white border border-slate-300 p-3 space-y-2">
            <div className="text-xs text-slate-600">
              Add to Google Authenticator / 1Password:
            </div>
            <div className="font-mono text-xs text-emerald-700 break-all">
              {uri}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="mfa-verify-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code"
                className="bg-white border border-slate-300 rounded-md px-2 py-1 text-sm flex-1"
              />
              <button
                data-testid="mfa-verify-btn"
                onClick={verify}
                className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-900 text-sm px-3 py-1"
              >
                Verify
              </button>
            </div>
          </div>
        )}
        {backup.length > 0 && (
          <div className="mt-3 rounded-md bg-amber-500/10 border border-amber-500/30 p-3">
            <div className="text-xs uppercase text-amber-300">
              Save these one-shot backup codes NOW
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2">
              {backup.map((c) => (
                <div
                  key={c}
                  data-testid={`mfa-backup-${c}`}
                  className="font-mono text-sm text-amber-100 bg-white rounded px-2 py-1"
                >
                  {c}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* IP Allowlist */}
      <section
        data-testid="ip-allowlist-section"
        className="rounded-xl border border-slate-200 bg-white p-5"
      >
        <div className="text-sm font-semibold">IP allowlist</div>
        <div className="text-xs text-slate-600 mt-1">
          Empty = no restriction. Add one or more CIDRs to lock down platform
          access to your networks.
        </div>
        <div className="mt-3 flex gap-2">
          <input
            data-testid="ip-cidr"
            value={newIp.cidr}
            onChange={(e) => setNewIp((s) => ({ ...s, cidr: e.target.value }))}
            placeholder="10.0.0.0/24 or 1.2.3.4"
            className="bg-white border border-slate-300 rounded-md px-2 py-1 text-sm flex-1"
          />
          <input
            data-testid="ip-label"
            value={newIp.label}
            onChange={(e) => setNewIp((s) => ({ ...s, label: e.target.value }))}
            placeholder="Label (office, home)"
            className="bg-white border border-slate-300 rounded-md px-2 py-1 text-sm w-52"
          />
          <button
            data-testid="ip-add-btn"
            onClick={addIp}
            className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-900 text-sm px-3 py-1 flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <div className="mt-3 space-y-1">
          {ips.map((r) => (
            <div
              key={r.id}
              data-testid={`ip-row-${r.id}`}
              className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
            >
              <div>
                <span className="font-mono text-slate-900">{r.cidr}</span>
                {r.label && (
                  <span className="ml-2 text-xs text-slate-500">
                    {r.label}
                  </span>
                )}
              </div>
              <button
                data-testid={`ip-del-${r.id}`}
                onClick={() => removeIp(r.id)}
                className="text-red-600 hover:text-red-300"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {ips.length === 0 && (
            <div className="text-xs text-slate-500">No entries — allowed from anywhere.</div>
          )}
        </div>
      </section>
    </div>
  );
}
